/**
 * CompanyContext.jsx - Context for B2B bedriftsdata
 * 
 * v2.0 - Lisensbegrensning
 * 
 * VIKTIG: RettVerk lagrer ALLE data lokalt på brukerens maskin.
 * 
 * Denne contexten:
 * - Henter bedriftsinfo fra API (kun metadata)
 * - Håndterer lisensierte moduler (bedriftens "lisenspott")
 * - Filtrerer moduler basert på lokal tilgang OG lisens
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import localAccessService from '../services/localAccessService';

const CompanyContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lokal lagring av lisensdata
const LICENSE_STORAGE_KEY = 'rettwerk_company_licenses';

// ═══════════════════════════════════════════════════════════════
// KOMPLETT MODULKATALOG (alle tilgjengelige moduler)
// ═══════════════════════════════════════════════════════════════
const ALL_AVAILABLE_MODULES = [
  // Oppvekst & Utdanning - Master Grade (36 verktøy totalt)
  { 
    key: 'barnehage', 
    name: 'Barnehage (Master Grade)', 
    category: 'Oppvekst & Utdanning',
    status: 'available', 
    icon: 'Baby', 
    premium: true,
    toolCount: 12,
    description: 'Juridisk støtte, saksbehandling og dokumentgeneratorer for barnehager',
    price: 4990
  },
  { 
    key: 'skole', 
    name: 'Skole (Master Grade)', 
    category: 'Oppvekst & Utdanning',
    status: 'available', 
    icon: 'GraduationCap', 
    premium: true,
    toolCount: 12,
    description: 'Juridisk støtte, saksbehandling og dokumentgeneratorer for skoler',
    price: 4990
  },
  { 
    key: 'barnevern-master', 
    name: 'Barnevern (Master Grade)', 
    category: 'Oppvekst & Utdanning',
    status: 'available', 
    icon: 'Heart', 
    premium: true,
    toolCount: 12,
    description: 'Komplett juridisk verktøykasse for barnevernstjenester',
    price: 5990
  },
  // Bedriftsmoduler
  { 
    key: 'arbeidsrett-bedrift', 
    name: 'Arbeidsrett – Bedrift', 
    category: 'Arbeidsrett',
    status: 'available', 
    icon: 'Briefcase', 
    premium: true,
    toolCount: 12,
    description: 'Ansettelse, oppsigelse, varsling og arbeidsavtaler',
    price: 3990
  },
  { 
    key: 'husleierett', 
    name: 'Husleierett', 
    category: 'Eiendom',
    status: 'available', 
    icon: 'Home',
    toolCount: 8,
    description: 'Leiekontrakter, oppsigelse og husleietvister',
    price: 2990
  },
  { 
    key: 'forbrukerrett', 
    name: 'Forbrukerrett', 
    category: 'Handel',
    status: 'available', 
    icon: 'ShoppingBag',
    toolCount: 8,
    description: 'Reklamasjon, garanti og forbrukerklager',
    price: 2990
  },
  { 
    key: 'barnevern', 
    name: 'Barnevern (Standard)', 
    category: 'Familie',
    status: 'available', 
    icon: 'Shield',
    toolCount: 6,
    description: 'Grunnleggende veiledning for barnevernssaker',
    price: 1990
  },
  // Kommende moduler
  { 
    key: 'gdpr', 
    name: 'GDPR-modul', 
    category: 'Compliance',
    status: 'available', 
    icon: 'Shield',
    toolCount: 10,
    description: 'Databehandleravtaler, personvernerklæringer, behandlingsprotokoller og avvikslogg',
    price: 4990
  },
  { 
    key: 'kontraktsbibliotek', 
    name: 'Kontraktsbibliotek', 
    category: 'Kontrakter',
    status: 'available', 
    icon: 'FileSignature',
    toolCount: 6,
    description: 'Arbeidskontrakter, leverandøravtaler, tjenesteavtaler, leieavtaler og NDA-maler',
    price: 3990
  },
  { 
    key: 'husleierett-naering', 
    name: 'Husleierett – Næring', 
    category: 'Eiendom',
    status: 'available', 
    icon: 'Building2',
    premium: true,
    toolCount: 8,
    description: 'Næringsleie, felleskostnader, MVA, KPI-regulering, vedlikehold og overtakelse',
    price: 4990
  },
  { 
    key: 'hms', 
    name: 'HMS', 
    category: 'Compliance',
    status: 'coming', 
    icon: 'Shield',
    toolCount: 10,
    description: 'Internkontroll, risikovurdering og HMS-dokumentasjon',
    price: 3990
  },
];

// Standard lisensierte moduler for nye bedrifter (demo)
const DEFAULT_LICENSED_MODULES = ['arbeidsrett-bedrift', 'husleierett', 'forbrukerrett', 'barnevern', 'barnehage', 'skole', 'barnevern-master', 'gdpr', 'kontraktsbibliotek', 'husleierett-naering'];

// Hent lisensdata fra localStorage
const getLicenseData = () => {
  try {
    const stored = localStorage.getItem(LICENSE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Lagre lisensdata til localStorage
const saveLicenseData = (data) => {
  try {
    localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch {
    return false;
  }
};

export function CompanyProvider({ children }) {
  const { user, token } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Lisensdata
  const [licenseData, setLicenseData] = useState(getLicenseData);
  
  // Lokal tilgangsdata (trigger re-render ved endring)
  const [accessVersion, setAccessVersion] = useState(0);

  // Hent brukerens rolle
  const getUserRole = useCallback(() => {
    // System admin is treated as admin
    if (user?.role === 'system_admin' || user?.is_system_admin) return 'system_admin';
    if (user?.company_role) return user.company_role;
    if (user?.role === 'company_admin' || user?.role === 'company_user') return user.role;
    if (user?.account_type === 'business') return 'company_admin';
    return 'company_user';
  }, [user]);

  const userRole = getUserRole();
  const isAdmin = userRole === 'company_admin' || userRole === 'system_admin';
  const isSystemAdmin = userRole === 'system_admin';
  const isPlatformAdmin = isAdmin && !!company?.platform_admin;

  // Hent bedriftsdata fra API (inkl. active_modules fra DB)
  const fetchCompanyData = useCallback(async () => {
    if (!user || !token || user.account_type !== 'business') {
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/company/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompany(res.data);
      setError(null);
      
      // Bruk active_modules fra DB som sannhetskilde for lisens
      const dbModules = res.data.active_modules || [];
      const newLicenseData = {
        companyId: res.data.id,
        licensedModules: dbModules,
        maxUsers: res.data.max_users || 10,
        plan: res.data.plan || 'professional',
        purchaseDate: new Date().toISOString(),
        renewalDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
      };
      saveLicenseData(newLicenseData);
      setLicenseData(newLicenseData);
    } catch (err) {
      // Company data not available — bruker standardverdier
      setCompany({
        id: user.id,
        name: user.name || 'Min Bedrift',
        plan: 'professional',
        max_users: 10
      });
      setError(null);
      
      // Ingen bedriftsdata — tom modulliste
      if (!licenseData) {
        const emptyLicenses = {
          companyId: user.id,
          licensedModules: [],
          maxUsers: 10,
          plan: 'professional',
          purchaseDate: new Date().toISOString(),
          renewalDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        };
        saveLicenseData(emptyLicenses);
        setLicenseData(emptyLicenses);
      }
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  // ═══════════════════════════════════════════════════════════════
  // LISENSIERTE MODULER (bedriftens "lisenspott")
  // ═══════════════════════════════════════════════════════════════
  
  // Hent liste over lisensierte modulnøkler
  const licensedModuleKeys = useMemo(() => {
    return licenseData?.licensedModules || DEFAULT_LICENSED_MODULES;
  }, [licenseData]);
  
  // Hent lisensierte moduler med full info
  const licensedModules = useMemo(() => {
    return ALL_AVAILABLE_MODULES.filter(m => 
      licensedModuleKeys.includes(m.key) && m.status !== 'coming'
    ).map(m => ({ ...m, status: 'active' }));
  }, [licensedModuleKeys]);
  
  // Hent alle tilgjengelige moduler for kjøp (ikke lisensiert ennå)
  const availableForPurchase = useMemo(() => {
    return ALL_AVAILABLE_MODULES.filter(m => 
      !licensedModuleKeys.includes(m.key)
    );
  }, [licensedModuleKeys]);
  
  // Sjekk om en modul er lisensiert
  const isModuleLicensed = useCallback((moduleKey) => {
    return licensedModuleKeys.includes(moduleKey);
  }, [licensedModuleKeys]);
  
  // Legg til ny lisens (simulerer kjøp)
  const addModuleLicense = useCallback((moduleKey) => {
    if (licensedModuleKeys.includes(moduleKey)) return false;
    
    const newLicenseData = {
      ...licenseData,
      licensedModules: [...licensedModuleKeys, moduleKey],
      lastPurchase: new Date().toISOString()
    };
    
    if (saveLicenseData(newLicenseData)) {
      setLicenseData(newLicenseData);
      setAccessVersion(v => v + 1);
      return true;
    }
    return false;
  }, [licenseData, licensedModuleKeys]);

  // ═══════════════════════════════════════════════════════════════
  // MODULTILGANG
  // ═══════════════════════════════════════════════════════════════
  
  // Hent brukerens modultilgang fra localStorage
  const userModuleAccess = useMemo(() => {
    if (!user?.id) return {};
    return localAccessService.getModuleAccess(user.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, accessVersion]);

  // Filtrer moduler basert på tilgang OG lisens
  const activeModules = useMemo(() => {
    // Admin ser alle LISENSIERTE moduler
    if (isAdmin) {
      return licensedModules;
    }
    
    // Vanlige brukere ser kun moduler de har tilgang til OG som er lisensiert
    const allowedKeys = localAccessService.getAllowedModules(
      user?.id,
      licensedModuleKeys,
      isAdmin
    );
    
    return licensedModules.filter(m => allowedKeys.includes(m.key));
  }, [licensedModules, licensedModuleKeys, isAdmin, user?.id, accessVersion]);

  // Sjekk om bruker har tilgang til en spesifikk modul
  const hasModuleAccess = useCallback((moduleKey) => {
    // Må være lisensiert først
    if (!licensedModuleKeys.includes(moduleKey)) return false;
    return localAccessService.hasModuleAccess(user?.id, moduleKey, isAdmin);
  }, [user?.id, isAdmin, licensedModuleKeys, accessVersion]);

  // Oppdater modultilgang (lagres lokalt) - kun for lisensierte moduler
  const updateModuleAccess = useCallback((userId, moduleKey, allowed) => {
    // Kun tillat tilgang til lisensierte moduler
    if (!licensedModuleKeys.includes(moduleKey)) {
      // Modul ikke lisensiert — ignorerer tilgangsoppdatering
      return false;
    }
    
    const success = localAccessService.setModuleAccess(userId, moduleKey, allowed);
    if (success) {
      setAccessVersion(v => v + 1);
    }
    return success;
  }, [licensedModuleKeys]);

  // Sett all modultilgang for en bruker
  const setAllUserModuleAccess = useCallback((userId, moduleAccess) => {
    // Filtrer bort ikke-lisensierte moduler
    const filteredAccess = {};
    Object.keys(moduleAccess).forEach(key => {
      if (licensedModuleKeys.includes(key)) {
        filteredAccess[key] = moduleAccess[key];
      }
    });
    
    const success = localAccessService.setAllModuleAccess(userId, filteredAccess);
    if (success) {
      setAccessVersion(v => v + 1);
    }
    return success;
  }, [licensedModuleKeys]);

  // Hent modultilgang for en spesifikk bruker
  const getUserModuleAccess = useCallback((userId) => {
    return localAccessService.getModuleAccess(userId);
  }, [accessVersion]);

  // Sett passord for en bruker (lokalt)
  const setUserPassword = useCallback(async (userId, password) => {
    return await localAccessService.setUserPassword(userId, password);
  }, []);

  // Sjekk om bruker har passord satt
  const hasPasswordSet = useCallback((userId) => {
    return localAccessService.hasPasswordSet(userId);
  }, [accessVersion]);

  // Refresh-funksjon
  const refetch = useCallback(async () => {
    setLoading(true);
    await fetchCompanyData();
    setAccessVersion(v => v + 1);
    setLoading(false);
  }, [fetchCompanyData]);

  const value = {
    // Bedriftsdata
    company,
    loading,
    error,
    
    // Lisensdata
    licenseData,
    licensedModuleKeys,
    licensedModules,
    availableForPurchase,
    isModuleLicensed,
    addModuleLicense,
    allAvailableModules: ALL_AVAILABLE_MODULES,
    
    // Moduler (filtrert basert på lisens)
    activeModules,
    companyModuleKeys: licensedModuleKeys,
    
    // Brukerinfo
    userRole,
    isAdmin,
    isPlatformAdmin,
    isSystemAdmin,
    
    // Tilgangsfunksjoner (lokalt)
    hasModuleAccess,
    updateModuleAccess,
    setAllUserModuleAccess,
    getUserModuleAccess,
    userModuleAccess,
    
    // Passordfunksjoner (lokalt)
    setUserPassword,
    hasPasswordSet,
    
    // Hjelpefunksjoner
    refetch
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}

export default CompanyContext;
